import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Container,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  InputAdornment
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  PieChart as PieChartIcon,
  Assessment as AssessmentIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AccountingService, ChartOfAccount, CreateAccountRequest } from '../services/accountingService';

interface Property {
  id: number;
  name: string;
}

interface ChartOfAccountsProps {
  properties: Property[];
  selectedPropertyId?: number;
  onPropertyChange: (propertyId: number | undefined) => void;
}

const ChartOfAccountsComponent: React.FC<ChartOfAccountsProps> = ({
  properties,
  selectedPropertyId,
  onPropertyChange
}) => {
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState<ChartOfAccount | null>(null);
  const [expandedAccounts, setExpandedAccounts] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAccountType, setFilterAccountType] = useState<string>('');
  const [createFormData, setCreateFormData] = useState<CreateAccountRequest>({
    code: '',
    name: '',
    account_type: 'ASSET',
    normal_balance: 'DEBIT',
    property_id: selectedPropertyId,
    description: ''
  });

  useEffect(() => {
    loadAccounts();
  }, [selectedPropertyId]);

  useEffect(() => {
    setCreateFormData(prev => ({
      ...prev,
      property_id: selectedPropertyId
    }));
  }, [selectedPropertyId]);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await AccountingService.getChartOfAccounts(selectedPropertyId);
      setAccounts(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load chart of accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await AccountingService.createAccount(createFormData);
      setShowCreateDialog(false);
      setCreateFormData({
        code: '',
        name: '',
        account_type: 'ASSET',
        normal_balance: 'DEBIT',
        property_id: selectedPropertyId,
        description: ''
      });
      loadAccounts();
    } catch (err: any) {
      setError(err.message || 'Failed to create account');
    }
  };

  const handleEditAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAccount) return;
    
    try {
      await AccountingService.updateAccount(editingAccount.id, createFormData);
      setEditingAccount(null);
      setCreateFormData({
        code: '',
        name: '',
        account_type: 'ASSET',
        normal_balance: 'DEBIT',
        property_id: selectedPropertyId,
        description: ''
      });
      loadAccounts();
    } catch (err: any) {
      setError(err.message || 'Failed to update account');
    }
  };

  const handleDeleteAccount = async (accountId: number) => {
    try {
      await AccountingService.deleteAccount(accountId);
      loadAccounts();
    } catch (err: any) {
      setError(err.message || 'Failed to delete account');
    }
  };

  const toggleAccountExpansion = (accountId: number) => {
    const newExpanded = new Set(expandedAccounts);
    if (newExpanded.has(accountId)) {
      newExpanded.delete(accountId);
    } else {
      newExpanded.add(accountId);
    }
    setExpandedAccounts(newExpanded);
  };

  const getAccountTypeOptions = () => [
    { value: 'ASSET', label: 'Asset' },
    { value: 'LIABILITY', label: 'Liability' },
    { value: 'EQUITY', label: 'Equity' },
    { value: 'REVENUE', label: 'Revenue' },
    { value: 'EXPENSE', label: 'Expense' },
    { value: 'CONTRA_ASSET', label: 'Contra Asset' },
    { value: 'CONTRA_LIABILITY', label: 'Contra Liability' },
    { value: 'CONTRA_EQUITY', label: 'Contra Equity' },
    { value: 'CONTRA_REVENUE', label: 'Contra Revenue' }
  ];

  const getAccountTypeColor = (type: string) => {
    switch (type) {
      case 'ASSET': return 'success';
      case 'LIABILITY': return 'error';
      case 'EQUITY': return 'info';
      case 'REVENUE': return 'primary';
      case 'EXPENSE': return 'warning';
      default: return 'default';
    }
  };

  const filteredAccounts = accounts.filter(account => {
    const matchesSearch = searchTerm === '' || 
      account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterAccountType === '' || account.account_type === filterAccountType;
    return matchesSearch && matchesType;
  });

  const startEdit = (account: ChartOfAccount) => {
    setEditingAccount(account);
    setCreateFormData({
      code: account.code,
      name: account.name,
      account_type: account.account_type,
      normal_balance: account.normal_balance,
      property_id: account.property_id,
      description: account.description || ''
    });
    setShowCreateDialog(true);
  };

  const resetForm = () => {
    setEditingAccount(null);
    setCreateFormData({
      code: '',
      name: '',
      account_type: 'ASSET',
      normal_balance: 'DEBIT',
      property_id: selectedPropertyId,
      description: ''
    });
  };

  const renderAccount = (account: ChartOfAccount, level: number = 0) => {
    const hasChildren = account.children && account.children.length > 0;
    const isExpanded = expandedAccounts.has(account.id);

    return (
      <React.Fragment key={account.id}>
        <TableRow
          sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
        >
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', ml: level * 3 }}>
              {hasChildren && (
                <IconButton
                  size="small"
                  onClick={() => toggleAccountExpansion(account.id)}
                  sx={{ mr: 1 }}
                >
                  {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </IconButton>
              )}
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                {account.code}
              </Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {account.name}
              </Typography>
              {account.description && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {account.description}
                </Typography>
              )}
            </Box>
          </TableCell>
          <TableCell>
            <Chip 
              label={account.account_type.replace('_', ' ')}
              color={getAccountTypeColor(account.account_type) as any}
              size="small"
              variant="outlined"
            />
          </TableCell>
          <TableCell>
            <Typography variant="body2">
              {account.normal_balance}
            </Typography>
          </TableCell>
          <TableCell>
            <Chip 
              label={account.is_active ? 'Active' : 'Inactive'}
              color={account.is_active ? 'success' : 'error'}
              size="small"
              variant="outlined"
            />
          </TableCell>
          <TableCell align="right">
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Edit Account">
                <IconButton 
                  size="small" 
                  color="primary"
                  onClick={() => startEdit(account)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {!account.is_system_account && (
                <Tooltip title="Delete Account">
                  <IconButton 
                    size="small" 
                    color="error"
                    onClick={() => handleDeleteAccount(account.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </TableCell>
        </TableRow>
        {hasChildren && isExpanded && (
          <AnimatePresence>
            {account.children?.map(child => renderAccount(child, level + 1))}
          </AnimatePresence>
        )}
      </React.Fragment>
    );
  };

  const getAccountSummary = () => {
    const summary = {
      total: accounts.length,
      active: accounts.filter(a => a.is_active).length,
      byType: {} as Record<string, number>
    };

    accounts.forEach(account => {
      summary.byType[account.account_type] = (summary.byType[account.account_type] || 0) + 1;
    });

    return summary;
  };

  const summary = getAccountSummary();

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Header Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {summary.total}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Accounts
                    </Typography>
                  </Box>
                  <AccountBalanceIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {summary.active}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Active Accounts
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {Object.keys(summary.byType).length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Account Types
                    </Typography>
                  </Box>
                  <PieChartIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              color: 'white'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {summary.byType['ASSET'] || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Asset Accounts
                    </Typography>
                  </Box>
                  <AssessmentIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Filters and Controls */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Paper 
            sx={{ 
              p: 3, 
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              mb: 4
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                placeholder="Search accounts..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  minWidth: 250,
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
              />
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Account Type</InputLabel>
                <Select
                  value={filterAccountType}
                  onChange={(e) => setFilterAccountType(e.target.value)}
                  label="Account Type"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {getAccountTypeOptions().map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ flexGrow: 1 }} />
              
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  resetForm();
                  setShowCreateDialog(true);
                }}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 3,
                  px: 3,
                  py: 1.5,
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                    transform: 'scale(1.05)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.3)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Add Account
              </Button>
            </Box>
          </Paper>
        </motion.div>

        {/* Accounts Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Paper sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Normal Balance</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAccounts.map(account => renderAccount(account))}
                  {filteredAccounts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {searchTerm || filterAccountType ? 'No accounts match your filters' : 'No accounts found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </motion.div>

        {/* Create/Edit Account Dialog */}
        <Dialog 
          open={showCreateDialog} 
          onClose={() => {
            setShowCreateDialog(false);
            resetForm();
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle
            sx={{
              background: 'linear-gradient(135deg, #1976d2, #9c27b0)',
              color: 'white',
              p: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {editingAccount ? 'Edit Account' : 'Create New Account'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5 }}>
                  {editingAccount ? 'Update account information' : 'Add a new account to your chart of accounts'}
                </Typography>
              </Box>
              <IconButton
                onClick={() => {
                  setShowCreateDialog(false);
                  resetForm();
                }}
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  '&:hover': {
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                <X size={24} />
              </IconButton>
            </Box>
          </DialogTitle>
          <form onSubmit={editingAccount ? handleEditAccount : handleCreateAccount}>
            <DialogContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Account Code"
                    value={createFormData.code}
                    onChange={(e) => setCreateFormData({...createFormData, code: e.target.value})}
                    required
                    variant="outlined"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Account Type</InputLabel>
                    <Select
                      value={createFormData.account_type}
                      onChange={(e) => setCreateFormData({...createFormData, account_type: e.target.value as any})}
                      label="Account Type"
                      sx={{ borderRadius: 2 }}
                    >
                      {getAccountTypeOptions().map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Account Name"
                    value={createFormData.name}
                    onChange={(e) => setCreateFormData({...createFormData, name: e.target.value})}
                    required
                    variant="outlined"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Normal Balance</InputLabel>
                    <Select
                      value={createFormData.normal_balance}
                      onChange={(e) => setCreateFormData({...createFormData, normal_balance: e.target.value as any})}
                      label="Normal Balance"
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="DEBIT">Debit</MenuItem>
                      <MenuItem value="CREDIT">Credit</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={createFormData.description}
                    onChange={(e) => setCreateFormData({...createFormData, description: e.target.value})}
                    multiline
                    rows={3}
                    variant="outlined"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 3, gap: 1 }}>
              <Button 
                onClick={() => {
                  setShowCreateDialog(false);
                  resetForm();
                }}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #1976d2, #9c27b0)',
                  borderRadius: 2,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0, #7b1fa2)',
                  }
                }}
              >
                {editingAccount ? 'Update' : 'Create'} Account
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </motion.div>
    </Container>
  );
};

export default ChartOfAccountsComponent;
